/**
 * Minecraft material -> texture path candidates.
 *
 * There is no manifest to consult at runtime, so instead of asking "does this
 * texture exist" we produce an ordered list of paths and let the <img> element
 * answer by loading or failing. Almost every material hits on the first
 * candidate; the chain only gets walked for the awkward ones.
 *
 * The rules come from an audit of the 1,061 distinct materials actually listed
 * on the server: raw name matching covered 815, and these derivations take it
 * to 1,053. The remainder are chests, heads and shields, which exist only as
 * entity models - those fall through to the initials badge, which is the honest
 * answer. A stand-in texture would be a *wrong* picture, and wrong icons were
 * the original complaint.
 */

const ASSET_REF = '1.21.11';
const BASE = `https://raw.githubusercontent.com/InventivetalentDev/minecraft-assets/${ASSET_REF}/assets/minecraft/textures`;

/** Suffixes whose texture comes from the block they are cut from. */
const CUT_FROM = [
  '_stairs',
  '_slab',
  '_wall',
  '_fence_gate',
  '_fence',
  '_button',
  '_pressure_plate',
  '_trapdoor',
  '_door',
];

/** Multi-face blocks whose flat name is not itself a texture. */
const FACES = ['_side', '_top', '_front', '_0', '_stage0', '_still'];

/**
 * Irregular names. Every entry here is the *same object* under another texture
 * name - an animated frame, a renamed block, or the face a flat block is drawn
 * from. Nothing here substitutes a different item for a missing one.
 */
const ALIASES: Record<string, string> = {
  clock: 'item/clock_00',
  compass: 'item/compass_00',
  recovery_compass: 'item/recovery_compass_00',
  crossbow: 'item/crossbow_standby',
  // Renamed in 1.21.9 when copper chains were added.
  chain: 'item/iron_chain',
  sticky_piston: 'block/piston_top_sticky',
  dried_kelp_block: 'block/dried_kelp_top',
  moss_carpet: 'block/moss_block',
  purpur_stairs: 'block/purpur_block',
  // The plate is drawn with the iron block texture in game.
  heavy_weighted_pressure_plate: 'block/iron_block',
  light_weighted_pressure_plate: 'block/gold_block',
  tipped_arrow: 'item/arrow',
  enchanted_golden_apple: 'item/golden_apple',
};

/** Ordered texture paths to try for a material, best guess first. */
export function textureCandidates(material: string): string[] {
  const out: string[] = [];
  const push = (path: string) => {
    if (!out.includes(path)) out.push(path);
  };

  let slug = material.toLowerCase();
  const alias = ALIASES[slug];
  if (alias) push(alias);

  const seen = new Set<string>();
  for (let step = 0; step < 6; step += 1) {
    if (seen.has(slug)) break;
    seen.add(slug);

    push(`item/${slug}`);
    push(`block/${slug}`);
    for (const face of FACES) push(`block/${slug}${face}`);

    // Every spawn egg shares one texture, tinted per mob at render time.
    if (slug.endsWith('_spawn_egg')) {
      push('item/spawn_egg');
      break;
    }

    const next = rewrite(slug);
    if (next == null) break;

    // A cut block has several plausible bases - planks for wood, the plural
    // form for "brick" - so all of them go in before moving on.
    if (Array.isArray(next)) {
      for (const candidate of next) {
        push(`item/${candidate}`);
        push(`block/${candidate}`);
      }
      // `rewrite` never returns an empty alternatives list, but the compiler
      // cannot know that from the type alone.
      const [first] = next;
      if (first == null) break;
      slug = first;
      continue;
    }
    slug = next;
  }

  return out;
}

/** One rewrite step, or a set of alternatives, or null when nothing applies. */
function rewrite(slug: string): string | string[] | null {
  if (slug.startsWith('waxed_')) return slug.slice(6);
  if (slug.startsWith('smooth_')) return slug.slice(7);
  if (slug.endsWith('_carpet')) return slug.replace(/_carpet$/, '_wool');
  if (slug.endsWith('_stained_glass_pane')) return slug.replace(/_pane$/, '');
  if (slug === 'glass_pane') return 'glass';
  if (slug.endsWith('_wood')) return slug.replace(/_wood$/, '_log');
  if (slug.endsWith('_hyphae')) return slug.replace(/_hyphae$/, '_stem');
  // Beds and banners are entity models; the dyed wool is the closest true
  // representation of the same colour and material.
  if (slug.endsWith('_bed')) return slug.replace(/_bed$/, '_wool');
  if (slug.endsWith('_banner')) return slug.replace(/_banner$/, '_wool');
  if (slug.endsWith('_hanging_sign')) return slug.replace(/_hanging_sign$/, '_planks');
  if (slug.endsWith('_sign')) return slug.replace(/_sign$/, '_planks');

  const cut = CUT_FROM.find((suffix) => slug.endsWith(suffix));
  if (cut) {
    const base = slug.slice(0, -cut.length);
    return [base, `${base}_planks`, `${base}s`, `${base}_bricks`];
  }

  if (slug.endsWith('_block')) return slug.slice(0, -6);
  if (slug.endsWith('brick')) return `${slug}s`;
  return null;
}

/** Full URL for a candidate path from {@link textureCandidates}. */
export function textureUrl(path: string): string {
  return `${BASE}/${path}.png`;
}
